import { useMutation, useQuery } from "@tanstack/react-query";
import useAxiosInstance from "./axiosInstance.utils";
import { errorFormat } from "./handleError.utils";
import { onSuccessMessage, queryClient } from "./reactQuery.utils";
import { deleteImageByUrls, filterImgFiles, uploadImage } from "./imageUpload.utils";
import { compact, difference, flatMap, map } from "lodash";
import { getJson, setJson } from "./json.utils";
import { arrayMap, isArray } from "./array.utils";
import { dateTime } from "./date.utils";
import { getItem, setItem } from "./localStorage.utils";
import { mileageKm } from "./number.utils";

/**
 * :::::::MUTATIONS:::::::::::
 */

export function useCreateStockListMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function createStockListApi(data) {
    try {
      const api = `/stock-list`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["createStockListApi", ...keyData],
    mutationFn: (data) => createStockListApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("stock-lists");
      onSuccessMessage(data, 201);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createStockList: mutateAsync };
}

export function useUpdateStockListMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function updateStockListApi(data) {
    try {
      const api = `/stock-list`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["updateStockListApi", ...keyData],
    mutationFn: (data) => updateStockListApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("stock-lists");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, updateStockList: mutateAsync };
}

export function useDeleteStockListMutation(keyData) {
  const { deleteApi } = useAxiosInstance();
  async function deleteStockListApi(id) {
    try {
      const api = `/stock-list/${id}`;
      const apiResponse = await deleteApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["deleteStockListApi", ...keyData],
    mutationFn: (data) => deleteStockListApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("stock-lists");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, deleteStockList: mutateAsync };
}

/**
 * :::::::QUERIES:::::::::::
 */
export function useGetAllStockListQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getAllStockListApi(query) {
    try {
      const api = `/stock-lists${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { data, ...rest } = useQuery({
    queryKey: [`getAllStockList-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getAllStockListApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  return { data: newData, ...rest };
}

export function useGetStockListQuery({ query, options = {}, key = "" }) {
  const { getApi } = useAxiosInstance();
  async function getStockListApi(query) {
    try {
      const api = `/stock-list${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { data, isPending: loading } = useQuery({
    queryKey: [`getStockList-${query ?? ""}${key ? "-" + key : ""}`],
    queryFn: () => getStockListApi(query),
    ...options
  });
  const newData = data?.data?.data ?? {};
  return { data: newData, loading };
}

// functions

export function getCarInfoBySlug(slug, carInfo) {
  return carInfo?.car_infos?.find((item) => item?.car_info_type?.slug === slug);
}

export function getAllCarInfoBySlug(slug, carInfo) {
  return carInfo?.car_infos?.filter((item) => item?.car_info_type?.slug === slug);
}

export async function imagesAdd({ chassis, images }) {
  let result = {
    status: true,
    images: []
  };
  if (!images?.length && !chassis) {
    return result;
  }

  const formData = new FormData();

  formData.append("folder", chassis);

  images.forEach((image) => formData.append("images[]", image));

  const imageUploaded = await uploadImage(formData);

  if (imageUploaded?.status !== 201) {
    onSuccessMessage(imageUploaded, 201);
    return result;
  }

  const uploadImages = imageUploaded?.data?.images ?? [];

  return { images: uploadImages, status: false };
}

export async function orderImagesUpload(chassis, images) {
  let uploadedImages = [];

  for (const image of images) {
    if (image && image?.type) {
      const data = await imagesAdd({ chassis, images: [image] });

      const src = data?.images?.length ? data?.images[0] : "";

      uploadedImages.push(src);
    } else {
      uploadedImages.push(image);
    }
  }

  return uploadedImages;
}

// image update based on chassis and delete images based on removed images from previous images
export async function imageUpdate({ chassis, images, prevImages }) {
  const needToDeleteImages = difference(prevImages, images);

  if (needToDeleteImages?.length) {
    const oldImageDelete = await deleteImageByUrls(needToDeleteImages);
    if (oldImageDelete?.status !== 200) {
      onSuccessMessage(oldImageDelete, 200);
      return true;
    }
  }

  const needToUpload = filterImgFiles(images);

  if (needToUpload?.length) {
    const imageUploaded = await imagesAdd({ chassis, images: needToUpload });

    if (imageUploaded.status) {
      return true;
    }
  }

  return false;
}

// delete images based on removed images from previous images
export async function deleteImages(images, prevImages) {
  const needToDeleteImages = difference(prevImages, images);

  if (needToDeleteImages?.length) {
    const oldImageDelete = await deleteImageByUrls(needToDeleteImages);
    if (oldImageDelete?.status !== 200) {
      onSuccessMessage(oldImageDelete, 200);
      return true;
    }
  }

  return false;
}

export function stockListFormatter(item) {
  const handleOption = (optionItem) => {
    return {
      label: optionItem?.name ?? "-",
      value: optionItem?.id
    };
  };

  const brand = handleOption(getCarInfoBySlug("brand", item));
  const model = handleOption(getCarInfoBySlug("model", item));
  const carPackage = handleOption(getCarInfoBySlug("package", item));
  const color = handleOption(getCarInfoBySlug("color", item));
  const cc = handleOption(getCarInfoBySlug("cubic-capacity-(cc)", item));
  const grade = handleOption(getCarInfoBySlug("auction-grade", item));
  const fuel = handleOption(getCarInfoBySlug("fuel", item));
  const chassis_code = handleOption(item?.chassis_code);
  const year = handleOption(getCarInfoBySlug("year", item));
  const location = handleOption(getCarInfoBySlug("car-location", item));
  const body_type = handleOption(getCarInfoBySlug("body-type", item));
  const forward = handleOption(getCarInfoBySlug("company", item));
  const features =
    arrayMap(getAllCarInfoBySlug("features", item), (item) => handleOption(item)) || [];
  const id = item?.id;
  const sku = item?.sku;
  const chassis = item?.chassis ?? "";
  const asking_price = item?.asking_price ?? "";
  const discount_price = item?.discount_price ?? "";
  const forward_price = item?.forward_price ?? "";
  const eta_date = item?.eta_date ? dateTime(item?.eta_date, "MMMM d, yyyy") : "";
  const mileage = item?.mileage ?? "";
  const images = item?.images ?? [];
  const auction_sheet = item?.auction_sheet ?? [];

  // inventory
  const inventory = getParsedAttachmentsByTypeId(item?.attachments, 3);

  // showroom works
  const showroom_works = getParsedAttachmentsByTypeId(item?.attachments, 4);

  // money receipts
  const parsed_money_receipts = getParsedAttachmentsByTypeId(item?.attachments, 2);
  const money_receipts = filterStockListAttachmentByTypeId(item?.attachments, 2);

  return {
    id,
    sku,
    chassis,
    chassis_code,
    brand,
    model,
    color,
    year,
    package: carPackage,
    grade,
    cc,
    fuel,
    features,
    asking_price,
    discount_price,
    location,
    forward,
    mileage,
    images,
    auction_sheet,
    forward_price,
    eta_date,
    body_type,
    inventory,
    showroom_works,
    money_receipts,
    parsed_money_receipts
  };
}

export function generateChassis(data) {
  const fullChassis =
    data?.chassis_code?.label && data?.chassis
      ? data?.chassis_code?.label + "-" + data?.chassis
      : null;

  return fullChassis;
}

export const locationNeedDateInput = ["ETA in Mongla", "ETA in Chittagong"];

export function extractValues(data) {
  const {
    chassis,
    chassis_code,
    features,
    asking_price,
    discount_price,
    forward_price,
    mileage,
    eta_date,
    max_mileage,
    min_mileage,
    max_date,
    min_date,
    max_price,
    min_price,
    t,
    ...rest
  } = data ?? {};

  // make full chassis name
  const fullChassis = chassis_code?.label && chassis ? chassis_code?.label + "-" + chassis : null;

  // Convert object values to an array
  const objectValuesArray = map(rest, "value");

  // Convert array of object values to an array
  const arrayValuesArray = flatMap(features, "value");

  const carInfos = compact([...objectValuesArray, ...arrayValuesArray]);

  const car_info_id = chassis_code?.value;
  // create or update values
  const values = {
    chassis,
    carInfos,
    car_info_id,
    asking_price: asking_price ? Number(asking_price) : null,
    discount_price: discount_price ? Number(discount_price) : null,
    forward_price: forward_price ? Number(forward_price) : null,
    eta_date: eta_date ? dateTime(eta_date, "yyyy-MM-dd") : null,
    mileage: mileage ? Number(mileage) : null,
    max_mileage: max_mileage ? Number(max_mileage) : null,
    min_mileage: min_mileage ? Number(min_mileage) : null,
    max_date: max_date ? dateTime(max_date, "yyyy-MM-dd") : null,
    min_date: min_date ? dateTime(min_date, "yyyy-MM-dd") : null,
    max_price: max_price ? Number(max_price) : null,
    min_price: min_price ? Number(min_price) : null,
    t
  };
  return {
    values,
    fullChassis
  };
}

export function filterValues() {
  const name = "stock-list-filter-values";
  return {
    setFilter: (data) => {
      const values = setJson(data);
      setItem(name, values);
    },
    getFilter: () => {
      const data = getItem(name);
      const values = getJson(data);
      return values;
    }
  };
}

// car info display for quotation formatter
export function displayCarInfo(data) {
  let formattedData = stockListFormatter(data);

  const images = formattedData?.images;

  const fullChassis = generateChassis(formattedData);

  const handleOption = (name) => {
    return getCarInfoBySlug(name, data ?? {})?.name ?? "-";
  };

  const brand = handleOption("brand");
  const model = handleOption("model");
  const carPackage = handleOption("package");
  const color = handleOption("color");
  const year = handleOption("year");
  const company = handleOption("company");
  const fuel = handleOption("fuel");
  const location = handleOption("car-location");
  const cc = handleOption("cubic-capacity-(cc)");
  const eta_date = data?.eta_date;
  const sku = data?.sku ?? "";
  const id = data?.id ?? "";
  const forward_price = data?.forward_price ?? 0;
  const discount_price = data?.discount_price ?? 0;
  const asking_price = data?.asking_price ?? 0;
  const mileage = data?.mileage;

  const title = `${brand} ${model} ${carPackage} ${year} ${color}`;
  const name = `${brand} ${model}`;

  const carInfos = data?.car_infos ?? [];

  const features =
    arrayMap(
      [...carInfos]?.filter((carInfo) => carInfo?.car_info_type?.slug === "features"),
      (item) => item?.name
    ) || [];

  return {
    images,
    fullChassis,
    brand,
    model,
    carPackage,
    color,
    year,
    company,
    location,
    eta_date,
    forward_price,
    discount_price,
    asking_price,
    mileage,
    title,
    features,
    name,
    sku,
    cc,
    fuel,
    id
  };
}

// vehicle infomatios
export function getVehicleInfos(data) {
  let formattedData = stockListFormatter(data);

  const images = formattedData?.images;

  const fullChassis = generateChassis(formattedData);

  const handleOption = (name) => {
    return getCarInfoBySlug(name, data ?? {})?.name ?? "-";
  };

  const brand = handleOption("brand");
  const model = handleOption("model");
  const carPackage = handleOption("package");
  const color = handleOption("color");
  const year = handleOption("year");
  const company = handleOption("company");
  const location = handleOption("car-location");
  const eta_date = data?.eta_date;
  const forward_price = data?.forward_price;
  const discount_price = data?.discount_price;
  const sku = data?.sku ?? "";
  const mileage = mileageKm(data?.mileage);

  const title = `${brand} ${model} ${carPackage} ${year} ${color}`;

  const carInfos = data?.car_infos ?? [];

  const auction_sheet = data?.auction_sheet
    ? isArray(getJson(data.auction_sheet))
      ? getJson(data.auction_sheet)
      : []
    : [];

  const features =
    arrayMap(
      [...carInfos]?.filter((carInfo) => carInfo?.car_info_type?.slug === "features"),
      (item) => item?.name
    ) || [];

  return {
    images,
    fullChassis,
    brand,
    model,
    carPackage,
    color,
    year,
    company,
    location,
    eta_date,
    forward_price,
    discount_price,
    mileage,
    title,
    carInfos,
    features,
    sku,
    auction_sheet
  };
}

// vehicle inventory formatter
export function customerAttachmentParser(data) {
  const attachment = getJson(data?.attachment) ?? null;

  return {
    ...data,
    attachment
  };
}

// filter stock list attachment by type id
export function filterStockListAttachmentByTypeId(data, typeId) {
  if (!data?.length) return [];

  return data.filter((attachment) => attachment?.type_id === typeId);
}

// get parsed attachments by type id
export function getParsedAttachmentsByTypeId(data, typeId) {
  if (!data?.length) return [];

  return arrayMap(filterStockListAttachmentByTypeId(data, typeId), (item) =>
    customerAttachmentParser(item)
  );
}
